import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { EDITOR_HOST_CLASS_PROP } from '@/hen-ui/catalog/editor-host';
import { Flex, type FlexProps } from '../Flex';

export interface FormProps
  extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'className'> {
  /** Space between the blocks. Uses the Flex gap scale. */
  gap?: FlexProps['gap'];
  /** Render as child element using Radix Slot */
  asChild?: boolean;
}

function preventDefaultSubmit(event: React.FormEvent<HTMLFormElement>) {
  event.preventDefault();
}

function partitionDomProps(props: FormProps): {
  domProps: FormProps;
  editorHostClassName?: string;
} {
  const record = props as FormProps & {
    className?: string;
    [EDITOR_HOST_CLASS_PROP]?: unknown;
  };
  const editorHostClassName = record[EDITOR_HOST_CLASS_PROP];
  const { className: _className, ...rest } = record;
  delete rest[EDITOR_HOST_CLASS_PROP];
  return {
    domProps: rest,
    editorHostClassName:
      typeof editorHostClassName === 'string' ? editorHostClassName : undefined,
  };
}

/**
 * Form - Column stack for fields, copy, and actions.
 *
 * Groups catalog blocks inside a semantic form. Spacing comes from Flex.
 * Sending the form stays on the page unless the caller passes `onSubmit`.
 *
 * @example
 * ```tsx
 * <Form gap="md" aria-label="Contact the seller">
 *   <Heading variant="h3">Contact the seller</Heading>
 *   <Input aria-label="First name" />
 *   <Button type="submit">Send</Button>
 * </Form>
 * ```
 */
export const Form = React.forwardRef<HTMLFormElement, FormProps>(
  ({ gap = 'md', asChild = false, children, ...props }, ref) => {
    const onSubmit = props.onSubmit ?? preventDefaultSubmit;
    const { domProps, editorHostClassName } = partitionDomProps(props);

    const sharedProps = {
      ...domProps,
      ...(editorHostClassName ? { className: editorHostClassName } : {}),
      'data-hen-id': COMPONENT_IDS.Form,
      onSubmit,
    };

    if (asChild) {
      return (
        <Flex asChild direction='column' gap={gap}>
          <Slot
            // Slot's ref is HTMLElement; the public ref is HTMLFormElement.
            ref={ref as React.Ref<HTMLElement>}
            {...sharedProps}
          >
            {children}
          </Slot>
        </Flex>
      );
    }

    return (
      <Flex asChild direction='column' gap={gap}>
        {/* hen-ui-allow-raw-html -- Form is the semantic form element. */}
        <form ref={ref} {...sharedProps}>
          {children}
        </form>
      </Flex>
    );
  },
);

Form.displayName = 'Form';
